import React, { useEffect, useRef } from 'react';
import { Award } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { experience, skillCategories } from '../data/about';
import SocialLinks from './SocialLinks';

gsap.registerPlugin(ScrollTrigger);

const About: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(headingRef.current, {
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 80%',
          end: 'top 50%',
          scrub: 1,
        },
        opacity: 0,
        y: 50,
      });

      gsap.from(contentRef.current, {
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 60%',
          end: 'top 30%',
          scrub: 1,
        },
        opacity: 0,
        y: 80,
      });

      if (timelineRef.current) {
        const items = timelineRef.current.children;
        gsap.from(Array.from(items), {
          scrollTrigger: {
            trigger: timelineRef.current,
            start: 'top 80%',
            end: 'top 40%',
            scrub: 1,
          },
          opacity: 0,
          x: -30,
          stagger: 0.15,
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="min-h-screen py-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-6xl mx-auto w-full">
        <h2
          ref={headingRef}
          className="font-mono text-sm text-accent uppercase tracking-[0.3em] mb-12"
        >
          {'// ABOUT ME'}
        </h2>

        <div ref={contentRef} className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
          {/* Left — headshot + social */}
          <div className="flex flex-col items-center lg:items-start gap-6">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-accent to-neon-cyan rounded-full opacity-50 group-hover:opacity-100 blur transition-opacity duration-500" />
              <img
                src="https://d204t2bnuk7ee7.cloudfront.net/assets/images/26017635.jpg"
                alt="Brandon Ellis"
                className="relative w-36 h-36 rounded-full object-cover border-2 border-dark-600"
              />
            </div>

            <SocialLinks />

            {/* Certifications */}
            <div className="w-full space-y-2 mt-4">
              <h4 className="font-mono text-xs text-gray-500 uppercase tracking-wider">Certifications</h4>
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <Award size={14} className="text-accent" />
                <span>AWS Certified Developer Associate</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <Award size={14} className="text-accent" />
                <span>AWS Certified Cloud Practitioner</span>
              </div>
            </div>
          </div>

          {/* Right — bio */}
          <div className="lg:col-span-2 space-y-6">
            <h3 className="text-2xl md:text-3xl font-bold">
              Senior Software Engineer <span className="text-accent">|</span> Full Stack
            </h3>

            <p className="text-gray-400 leading-relaxed text-lg">
              I am a senior software engineer with <span className="text-accent">6+ years</span> of experience delivering scalable software across industries — from <span className="text-accent">financial services</span> and insurance to <span className="text-accent">IoT/EV charging</span> infrastructure and enterprise analytics. I specialize in full-stack development on AWS, building cloud-native applications with serverless, event-driven architectures.
            </p>

            <p className="text-gray-400 leading-relaxed text-lg">
              Currently at <span className="text-accent">Symetra Financial</span>, I build cross-division custom software solutions serving their Life and Benefits divisions. Previously, I led a small engineering team at <span className="text-accent">Panasonic</span> shipping SaaS products for EV smart charging and fleet management. I also run <span className="text-accent">BT Digital</span>, a consulting practice focused on software development for nonprofits and small businesses.
            </p>

            {/* Skills grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
              {skillCategories.map(({ label, skills }) => (
                <div key={label} className="space-y-2">
                  <h4 className="font-mono text-xs text-gray-500 uppercase tracking-wider">{label}</h4>
                  <div className="flex flex-wrap gap-2">
                    {skills.map((skill) => (
                      <span
                        key={skill}
                        className="px-2.5 py-1 text-xs font-mono text-accent border border-accent/30 rounded-md bg-accent/5 hover:bg-accent/10 hover:border-accent/60 transition-all duration-200"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Experience Timeline */}
        <div className="mt-20">
          <h3 className="font-mono text-sm text-accent uppercase tracking-[0.3em] mb-8">
            {'// EXPERIENCE'}
          </h3>
          <div ref={timelineRef} className="relative space-y-0">
            {/* Vertical line */}
            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-accent/20" />

            {experience.map((exp) => (
              <div key={`${exp.title}-${exp.company}`} className="relative pl-8 pb-8 last:pb-0">
                {/* Dot */}
                <div className={`absolute left-0 top-2 w-[15px] h-[15px] rounded-full border-2 ${
                  exp.current
                    ? 'border-accent bg-accent/30 shadow-neon'
                    : 'border-gray-600 bg-dark-800'
                }`} />

                <div className="glass p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                    <h4 className="text-white font-semibold">{exp.title}</h4>
                    <span className="font-mono text-xs text-accent">{exp.period}</span>
                  </div>
                  <p className="text-gray-400 text-sm mt-1">{exp.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
